<template>
  <div class="h-full">
    <div class="h-full flex flex-col">
      <div class="flex w-full pb-3 items-center flex-shrink-0 sm:pr-10">
        <div class="flex-grow flex items-center">
          <div class="text-13 font-600">{{ $t('projects.variables.title') }}</div>
        </div>
        <div class="ml-auto flex flex-row justify-center">
          <VDropdown theme="fidsy" class="inline" placement="bottom-end" :distance="-5" :triggers="['click']" auto-hide>
            <IconButton icon="tune" class="mr-2" />
            <template #popper>
              <div class="w-200 bg-on-primary">
                <div class="px-4 pt-3 pb-2 font-500 text-13 tracking-wide text-semi-dark-text">Filter Tag</div>
                <div class="px-4 py-2">
                  <div v-for="tag in tagFilters" :key="tag" class="my-3" @click.stop.prevent="onFilterTagClick(tag)">
                    <Checkbox :model-value="tagFilter.includes(tag)" :label="tag" class="!text-13" />
                  </div>
                </div>
              </div>
            </template>
          </VDropdown>
          <PillButton :text="$t('add_new')" primary @click="onCreateClicked" />
        </div>
      </div>
      <div class="w-full flex-grow overflow-hidden pb-2">
        <TableElevated :items="variables" key="version" :headers="variableHeaders" class="text-13 pr-3 sm:pr-10">
          <template v-slot:[`item.name`]="{ value: variableName }">
            <span class="text-13 font-500 truncate">{{ variableName }}</span>
          </template>
          <template v-slot:[`item.dataType`]="{ value }">
            <Badge :text="value" small gray class="ml-1 px-1 uppercase"></Badge>
          </template>
          <template v-slot:[`item.tags`]="{ item }">
            <Badge v-for="tag in item.tags" :key="tag" :text="tag" :error="tag === 'readonly'" :info="tag === 'system'" :success="tag === 'user'" small class="ml-1 px-1 uppercase"></Badge>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <div class="flex flex-row justify-end items-center ml-auto">
              <IconButton :disabled="variableDetails[item.name].tags.includes('system')" icon="edit" class="sm:mr-1 w-auto text-primary" @click="onEditClicked(item.name)" />
              <IconButton :disabled="variableDetails[item.name].tags.includes('system')" icon="delete" class="text-primary" @click="onDeleteClicked(item)" :text="$t('delete')" />
            </div>
          </template>
        </TableElevated>
      </div>
    </div>
    <Modal v-if="fetching">
      <div class="flex flex-col font-700 items-center justify-center px-20 my-10">
        <div>{{ $t('loading_data') }}</div>
        <Icon name="loading_dots" class="w-14 mt-3 text-primary" />
      </div>
    </Modal>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { sortByKey } from '@/helpers/array';
import DeleteModal from '../../modals/DeleteModal.vue';
import CreateProjectVariable from '../../project/CreateProjectVariable';

export default {
  name: 'NoCodeVariables',
  data() {
    return {
      fetching: false,
      tagFilter: ['user'],
    };
  },
  computed: {
    ...mapState(['projectData']),
    ...mapGetters(['getProjectVariables']),
    variableHeaders() {
      return [
        { key: 'name', title: this.$t('name') },
        { key: 'tags', title: this.$t('tags'), width: '140px' },
        { key: 'dataType', title: this.$t('data_type'), width: '100px' },
        { key: 'actions', title: this.$t('actions'), width: '50px' },
      ];
    },
    tagFilters() {
      // const tags = new Set();
      return Object.values(this.getProjectVariables(this.projectId))
        .reduce((acc, variable) => {
          (variable.tags || []).forEach((tag) => {
            if (!acc.includes(tag)) {
              acc.push(tag);
            }
          });
          return acc;
        }, [])
        .filter((t) => t !== 'templateonly');
    },
    variables() {
      if (this.projectId && Object.keys(this.projectData).length) {
        let variables = Object.keys(this.getProjectVariables(this.projectId)).filter((v) => !(this.variableDetails[v].tags || []).includes('templateonly'));
        variables = variables.filter((v) => this.tagFilter.some((tag) => this.variableDetails[v].tags.includes(tag)));
        return sortByKey(
          variables.map((v) => ({
            name: v,
            dataType: this.variableDetails[v].dataType,
            tags: this.variableDetails[v].tags || [],
          })),
        );
      }
      return [];
    },
    variableDetails() {
      return this.getProjectVariables(this.projectId);
    },
    projectId() {
      return this.$route.params.projectId;
    },
  },
  methods: {
    ...mapActions(['showToastMessage', 'fetchProjectVariables', 'deleteProjectVariable']),
    onFilterTagClick(tag) {
      if (this.tagFilter.includes(tag)) {
        this.tagFilter = this.tagFilter.filter((t) => t !== tag);
      } else {
        this.tagFilter.push(tag);
      }
    },
    onCreateClicked() {
      const modal = this.$showModal(
        CreateProjectVariable,
        {
          projectId: this.projectId,
        },
        {
          save: () => {
            this.onVariableSaved(modal);
          },
        },
      );
    },
    onEditClicked(variableName) {
      this.$showModal(CreateProjectVariable, {
        projectId: this.projectId,
        variableName,
      });
    },
    onDeleteClicked(variable) {
      this.$showModal(DeleteModal, {
        subtitle: this.$t('projects.remove_variable', { variableName: variable.name }),
        info: null,
        onConfirm: async () => {
          await this.deleteProjectVariable({ name: variable.name, project_id: this.projectId, dataType: variable.dataType });
          this.showToastMessage({ message: this.$t('projects.variable_removed_successfully'), type: 'success' });
        },
      });
    },
  },
  watch: {
    projectId: {
      async handler() {
        this.fetching = true;
        await this.fetchProjectVariables({ projectId: this.projectId });
        this.fetching = false;
      },
      immediate: true,
    },
  },
};
</script>
